import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

const STATUSES = ['active', 'inactive'];

function formatValuePreview(row) {
  if (row.value_masked) {
    return row.has_value ? '••••••• (hidden)' : '—';
  }
  if (row.setting_value === null || row.setting_value === undefined) return '—';
  if (row.setting_type === 'boolean') return row.setting_value ? 'true' : 'false';
  if (row.setting_type === 'json') {
    try {
      return JSON.stringify(row.setting_value).slice(0, 80);
    } catch (_e) {
      return String(row.setting_value);
    }
  }
  const s = String(row.setting_value);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}

export default function TenantSystemSettingsListPage() {
  const navigate = useNavigate();
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [groupInput, setGroupInput] = useState('');
  const [appliedGroup, setAppliedGroup] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [sort, setSort] = useState('setting_group');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedGroup) params.set('settingGroup', appliedGroup);
    if (appliedStatus) params.set('status', appliedStatus);
    try {
      const res = await tenantApiFetch(`/api/tenant/system-settings?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load settings');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedGroup, appliedStatus, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedGroup(groupInput);
    setAppliedStatus(statusInput);
  };

  const entries = data?.entries || [];
  const groups = data?.groups || [];
  const total = data?.pagination?.total ?? 0;

  const byGroup = entries.reduce((acc, row) => {
    const g = row.setting_group || 'default';
    if (!acc[g]) acc[g] = [];
    acc[g].push(row);
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Tenant settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Key/value configuration for your organization. Viewers see masked sensitive values; admins see full values
            and can edit.
          </Typography>
        </Box>
        <TenantCan permission="tenant.system_settings.manage">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tenant/system-settings/new')}>
            New setting
          </Button>
        </TenantCan>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            sx={{ minWidth: 200 }}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Group</InputLabel>
            <Select label="Group" value={groupInput} onChange={(e) => setGroupInput(e.target.value)}>
              <MenuItem value="">All groups</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="setting_group">Group</MenuItem>
              <MenuItem value="updated_at">Updated</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="asc">Asc</MenuItem>
              <MenuItem value="desc">Desc</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {Object.keys(byGroup)
              .sort()
              .map((g) => (
                <Box key={g} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ px: 2, pt: 2, pb: 1, bgcolor: 'grey.100' }}>
                    {g}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Key</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Value preview</TableCell>
                        <TableCell>Flags</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byGroup[g].map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.setting_key}</TableCell>
                          <TableCell>{row.setting_label}</TableCell>
                          <TableCell>{row.setting_type}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>{formatValuePreview(row)}</TableCell>
                          <TableCell>
                            {row.is_sensitive && <Chip size="small" label="Sensitive" color="warning" />}
                          </TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell align="right">
                            <IconButton component={Link} to={`/tenant/system-settings/${row.id}`} size="small">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <TenantCan permission="tenant.system_settings.manage">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/tenant/system-settings/${row.id}/edit`)}
                                aria-label="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TenantCan>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            {entries.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No settings yet.</Typography>
              </Box>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
